/**
 * Newsletters — drafts, scheduled, and what has been sent.
 *
 * All one collection, `skidaCrmEmailSends`, because they are one thing at
 * different points in its life. The design's list mixes them freely and its
 * modal switches on `isDraftOrScheduled`; two collections would mean a union
 * query and a document that has to move between them at the worst possible
 * moment — mid-send.
 *
 * ## What a client may write, and what it may not
 *
 * Drafts and scheduled newsletters are written from the browser. Anything sent
 * is written only by the Cloud Function. `firestore.rules` enforces that, and
 * the reason is that a sent record is the audit trail — what went out, to whom,
 * who authorised it, which test unlocked it. This module never attempts to
 * touch one; the rules are there for when somebody else's code does.
 *
 * ## Ordering
 *
 * By `createdAt`, which every document has, rather than `startedAt`, which only
 * a send that started has. Ordering by `startedAt` would have dropped every
 * draft out of the list silently — the query would simply not return them.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import type { DocumentData } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { EMPTY_DOCUMENT, type EmailBlock } from '@/types/emailBlocks'
import { renderEmail } from '@/shared/email/renderEmail'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'

export type NewsletterStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'partial'
  | 'finished'
  | 'finished-with-errors'

export interface Newsletter {
  id: string
  /**
   * What staff call it — 14 Oct 2026. The subject is what readers see; two
   * drafts can share one ("Your Premium access ends tomorrow" in two
   * languages) and still need telling apart. Falls back to the subject on a
   * document written before names existed.
   */
  name: string
  subject: string
  /** The inbox preview line; also rendered into `html`. */
  preheader: string
  html: string
  /** The sections it was composed from. Empty on anything written before them. */
  blocks: EmailBlock[]
  /**
   * Every audience the send names — 17 Sep 2026.
   *
   * `audience` is the first of them, kept so nothing that reads a single value
   * breaks. A send written before this has one audience and no list; a reader
   * should prefer this and fall back.
   */
  audiences?: string[]
  audience: string
  /** Countries and languages the send keeps to — 27 Oct 2026. Null is everybody. */
  reach?: Reach | null
  kind: 'service' | 'marketing'
  sender: 'consumer' | 'pro'
  status: NewsletterStatus
  savedBy?: string
  sentBy?: string
  /** When a scheduled newsletter goes out. Null on a draft. */
  scheduledFor?: Date | null
  /** Set once a send has started. Absent on a draft. */
  startedAt?: Date | null
  createdAt?: Date | null
  total?: number
  sent?: number
  delivered?: number
  opened?: number
  clicked?: number
  bounced?: number
  complained?: number
  /**
   * Opt-outs traced back to this campaign — 21 Sep 2026.
   *
   * `unsubscribed` is everybody who turned something off; `unsubscribedAll` the
   * subset who turned off everything. Both are counted by `crmUnsubscribe` from
   * the send id carried in the footer link, so they are absent on anything sent
   * before that link existed — which is a gap in the record, not a campaign that
   * nobody left.
   */
  unsubscribed?: number
  unsubscribedAll?: number
  /**
   * Set while a large send waits for the daily budget — 13 Oct 2026.
   *
   * The server spreads a send over as many days as it takes so a sending domain
   * stays under Google's bulk threshold; see `functions-webhook/src/sendQuota.ts`.
   * This is when it next has room. Absent or past means it is not waiting.
   */
  waitingUntil?: Date | null
  /** When the last pass ran — for a finished send, when it finished. */
  finishedAt?: Date | null
  /** On a resend to non-openers: the newsletter it resent. 23 Oct 2026. */
  resendOf?: string | null
  /** On the original: the resend made from it, and when. */
  resentAs?: string | null
  resentAt?: Date | null
  /** The per-24-hours budget the waiting refers to. */
  dailyLimit?: number
}

/**
 * "Paused by the daily limit", for a partial send that is waiting.
 *
 * One wording for the three places a send's status is shown, so the list, the
 * log and the dialog cannot disagree about why a send has stopped.
 */
export function dailyLimitNote(n: Newsletter, now = new Date()): string | null {
  if (n.status !== 'partial' || !n.waitingUntil || n.waitingUntil <= now) return null
  const when = n.waitingUntil.toLocaleString('nb-NO', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
  const limit = n.dailyLimit ? `${n.dailyLimit.toLocaleString('nb-NO')} per 24 hours` : 'the daily limit'
  return `Spread over days to stay under Google's bulk limit (${limit}). Continues ${when}.`
}

/**
 * One destination in one campaign, and how often it was pressed.
 *
 * `clicks` counts presses; `reach` counts messages that produced at least one,
 * which is the figure to compare two links by. Neither says who — see the note
 * in `resendWebhook.ts`.
 */
export interface SendLink {
  id: string
  url: string
  clicks: number
  reach: number
}

/** A draft has never been sent, so it has no figures and no send date. */
export const isUnsent = (n: Newsletter) => n.status === 'draft' || n.status === 'scheduled'

const toDate = (v: unknown): Date | null =>
  v && typeof (v as { toDate?: () => Date }).toDate === 'function'
    ? (v as { toDate: () => Date }).toDate()
    : null

/**
 * A send's country and language filter — 27 Oct 2026. The engine's
 * `reach.ts` is the authority; this only reads back what was saved.
 */
export interface Reach {
  countries?: string[]
  languages?: string[]
}

function reachOf(v: unknown): Reach | null {
  if (!v || typeof v !== 'object') return null
  const r = v as { countries?: unknown; languages?: unknown }
  const list = (x: unknown) => (Array.isArray(x) ? x.map(String) : [])
  const countries = list(r.countries)
  const languages = list(r.languages)
  return countries.length || languages.length ? { countries, languages } : null
}

/** One document of the collection, as the screens read it. */
function newsletterOf(id: string, v: DocumentData): Newsletter {
  return {
    id,
    name: (typeof v.name === 'string' && v.name.trim()) || v.subject || '',
    subject: v.subject ?? '',
    preheader: typeof v.preheader === 'string' ? v.preheader : '',
    html: v.html ?? '',
    blocks: Array.isArray(v.blocks) ? (v.blocks as EmailBlock[]) : [],
    audiences: Array.isArray(v.audiences) ? (v.audiences as string[]) : undefined,
    audience: v.audience ?? '',
    reach: reachOf(v.reach),
    scheduledFor: toDate(v.scheduledFor),
    kind: v.kind === 'service' ? 'service' : 'marketing',
    sender: v.sender === 'pro' ? 'pro' : 'consumer',
    // A document written before drafts existed has no status of its
    // own only if something went wrong; default to finished rather than
    // draft, so an old send can never become editable by omission.
    status: (v.status ?? 'finished') as NewsletterStatus,
    savedBy: v.savedBy ?? '',
    sentBy: v.sentBy ?? '',
    startedAt: toDate(v.startedAt),
    createdAt: toDate(v.createdAt) ?? toDate(v.startedAt),
    total: v.total,
    sent: v.sent,
    delivered: v.delivered,
    opened: v.opened,
    clicked: v.clicked,
    bounced: v.bounced,
    complained: v.complained,
    unsubscribed: v.unsubscribed,
    unsubscribedAll: v.unsubscribedAll,
    waitingUntil: toDate(v.waitingUntil),
    finishedAt: toDate(v.finishedAt),
    resendOf: typeof v.resendOf === 'string' ? v.resendOf : null,
    resentAs: typeof v.resentAs === 'string' ? v.resentAs : null,
    resentAt: toDate(v.resentAt),
    dailyLimit: typeof v.dailyLimit === 'number' ? v.dailyLimit : undefined,
  }
}

/**
 * The newsletters, newest first.
 *
 * `sender` narrows to one identity; `'all'` is the default since 17 Sep 2026,
 * when the product switch went away. A send can now reach app users and PRO
 * companies at once, so a log filtered to one of them would hide the sends that
 * are the point of the change.
 *
 * The share register's own mail is filtered out on read: it lives in the same
 * collection and has its own log on the Ownership screen, and 44 000-person
 * campaigns listed beside a letter to thirty-eight investors serve neither.
 */
export function subscribeToNewsletters(
  sender: 'consumer' | 'pro' | 'all',
  onData: (rows: Newsletter[]) => void,
  onError: (e: Error) => void,
  /** The newest this many. The log asks for more than a picker needs. */
  max = 50,
): () => void {
  const q =
    sender === 'all'
      ? query(
          collection(db, CRM_COLLECTIONS.emailSends),
          orderBy('createdAt', 'desc'),
          limit(max),
        )
      : query(
          collection(db, CRM_COLLECTIONS.emailSends),
          where('sender', '==', sender),
          orderBy('createdAt', 'desc'),
          limit(max),
        )
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs.map((d) => newsletterOf(d.id, d.data())),
      ),
    (e) => onError(e as Error),
  )
}

/** One newsletter by id, or null — the automation screen links drafts here. */
export async function loadNewsletter(id: string): Promise<Newsletter | null> {
  const snap = await getDoc(doc(db, CRM_COLLECTIONS.emailSends, id))
  return snap.exists() ? newsletterOf(snap.id, snap.data()) : null
}

export interface DraftInput {
  /** Several, deduplicated on the server. `audience` is the first of them. */
  audiences?: string[]
  /**
   * The internal name; the subject is used when it is left out. Optional so a
   * caller that only reschedules a draft leaves its name as it was.
   */
  name?: string
  subject: string
  /** Left out keeps what is stored, like `name`. */
  preheader?: string
  /** The rendered message — what is sent, hashed and tested. */
  html: string
  /**
   * The sections it was built from, so reopening a draft reopens the editor
   * rather than a wall of table markup.
   *
   * The HTML beside it is the artefact; this is the source. They are written
   * together and only ever together — `renderEmail(blocks)` is `html`, and a
   * draft where the two disagree would preview one message and send another.
   */
  blocks: EmailBlock[]
  audience: string
  /** Countries and languages; null for everybody. Left out keeps what is stored. */
  reach?: Reach | null
  kind: 'service' | 'marketing'
  sender: 'consumer' | 'pro'
  savedBy: string
}

export async function createDraft(input: DraftInput): Promise<string> {
  const ref = await addDoc(collection(db, CRM_COLLECTIONS.emailSends), {
    ...input,
    name: input.name?.trim() || input.subject,
    preheader: input.preheader ?? '',
    status: 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

/**
 * Bring a draft's stored HTML up to the current email design — 18 Oct 2026.
 *
 * A draft keeps the HTML it rendered to when last saved, so a change to a
 * block's design (the footer's social links, for one) would otherwise reach
 * it only when somebody opened and saved it. The list does that save for
 * every draft it shows: the composer's own `renderEmail` over the draft's
 * blocks and preview text, written only when the result differs. Drafts only
 * — a scheduled send is tested against a hash of its HTML and must keep it.
 * Returns how many were rewritten.
 */
export async function refreshDraftHtml(drafts: Newsletter[]): Promise<number> {
  let n = 0
  for (const d of drafts) {
    if (d.status !== 'draft' || d.blocks.length === 0) continue
    const html = renderEmail({ ...EMPTY_DOCUMENT, blocks: d.blocks, preheader: d.preheader })
    if (html === d.html) continue
    await updateDoc(doc(db, CRM_COLLECTIONS.emailSends, d.id), { html, updatedAt: serverTimestamp() })
    n++
  }
  return n
}

export async function updateDraft(id: string, input: DraftInput): Promise<void> {
  // `name` left out keeps the stored one; Firestore refuses an undefined field.
  const { name, preheader, ...rest } = input
  await updateDoc(doc(db, CRM_COLLECTIONS.emailSends, id), {
    ...rest,
    ...(name !== undefined ? { name: name.trim() || input.subject } : {}),
    ...(preheader !== undefined ? { preheader } : {}),
    // Saving takes a scheduled newsletter off the schedule — its test was of
    // the content before the edit — so the time goes too, rather than staying
    // behind on a draft as if it still meant something.
    status: 'draft',
    scheduledFor: null,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Copy a newsletter into a new draft.
 *
 * Deliberately does not carry the figures across, and cannot: the rules refuse
 * a client-written document that has them. A duplicate is the words, not the
 * campaign — the whole point is to send them again to somebody else.
 */
export async function duplicateNewsletter(n: Newsletter, savedBy: string): Promise<string> {
  return createDraft({
    name: `${n.name} (copy)`,
    subject: n.subject,
    preheader: n.preheader,
    html: n.html,
    blocks: n.blocks,
    audience: n.audience,
    kind: n.kind,
    sender: n.sender,
    savedBy,
  })
}

export async function deleteDraft(id: string): Promise<void> {
  await deleteDoc(doc(db, CRM_COLLECTIONS.emailSends, id))
}

/**
 * Schedule a newsletter to send later.
 *
 * Writes the content and a time; the audience is deliberately *not* frozen.
 * `crmTick` (sendScheduled.ts) resolves it when the send comes due, so what was approved
 * is the group rather than a headcount taken on the day it was written —
 * people who subscribe in between are included, and people who opt out, bounce
 * or report us as spam are not.
 *
 * The content-hashed test still applies, and the scheduler checks it itself
 * rather than trusting this document: the rules can say "a member of staff
 * wrote this", but not "and this exact content was tested".
 */
export async function scheduleNewsletter(
  id: string | null,
  input: DraftInput,
  when: Date,
): Promise<string> {
  // `name` and `preheader` left out keep what is stored, as in `updateDraft`.
  const { name, preheader, ...rest } = input
  const payload = {
    ...rest,
    ...(name !== undefined ? { name: name.trim() || input.subject } : {}),
    ...(preheader !== undefined ? { preheader } : {}),
    status: 'scheduled',
    scheduledFor: when,
    updatedAt: serverTimestamp(),
  }
  if (id) {
    await updateDoc(doc(db, CRM_COLLECTIONS.emailSends, id), payload)
    return id
  }
  const ref = await addDoc(collection(db, CRM_COLLECTIONS.emailSends), {
    ...payload,
    createdAt: serverTimestamp(),
  })
  return ref.id
}


/**
 * Which links a campaign's readers actually pressed.
 *
 * Read on demand rather than with the log: it is a subcollection per send, and
 * the list shows thirty sends at a time. Nobody needs thirty link tables to
 * decide which row to open.
 */
export async function loadSendLinks(sendId: string): Promise<SendLink[]> {
  const snap = await getDocs(collection(db, CRM_COLLECTIONS.emailSends, sendId, 'links'))
  return snap.docs
    .map((d) => ({
      id: d.id,
      url: String(d.get('url') ?? ''),
      clicks: Number(d.get('clicks')) || 0,
      reach: Number(d.get('reach')) || 0,
    }))
    .sort((a, b) => b.reach - a.reach || b.clicks - a.clicks)
}
